<template>
	<div class="search-panel" :class="{ 'search-panel--en': locale === 'en', 'is-loading': loading }">
		<div v-if="loading" class="panel-loading-skeleton" aria-hidden="true">
			<span class="panel-loading-skeleton__search"></span>
			<span class="panel-loading-skeleton__heading"></span>
			<div class="panel-loading-skeleton__categories">
				<span v-for="item in 3" :key="item"></span>
			</div>
			<span class="panel-loading-skeleton__map"></span>
			<span class="panel-loading-skeleton__result-title"></span>
			<div v-for="item in 3" :key="`result-${item}`" class="panel-loading-skeleton__card">
				<span class="panel-loading-skeleton__cover"></span>
				<div>
					<span class="panel-loading-skeleton__line panel-loading-skeleton__line--title"></span>
					<span class="panel-loading-skeleton__line panel-loading-skeleton__line--meta"></span>
					<span class="panel-loading-skeleton__line"></span>
				</div>
			</div>
		</div>
		<div class="search-bar">
			<img class="search-icon" :src="iconSearch" alt="" aria-hidden="true" />
			<input
				v-model="keyword"
				type="search"
				class="search-input"
				:placeholder="placeholder"
				:disabled="loading"
				@keydown.enter="handleSearch"
			/>
			<span class="search-divider" aria-hidden="true"></span>
			<button type="button" class="search-btn" :disabled="loading" @click="handleSearch">
				{{ searchLabel }}
			</button>
		</div>

		<div class="category-section">
			<div class="category-title">
				<img :src="iconHot" alt="" aria-hidden="true" />
				<span>{{ t(`${I18N_NS}.hotCategories`) }}</span>
			</div>
			<div class="category-scroll">
				<button
					v-for="item in categories"
					:key="item.id || item.label"
					type="button"
					class="category-btn"
					:class="{
						'is-active': String(item.id) === String(activeCategoryId),
						'is-nearby': String(item.id) === 'nearby',
					}"
					@click="handleCategoryClick(item)"
				>
					<img class="category-icon" :src="resolveCategoryIcon(item)" alt="" aria-hidden="true" />
					<span class="category-btn-label">{{ getCategoryLabel(item) }}</span>
					<img
						v-if="String(item.id) === 'nearby'"
						class="category-caret"
						:src="iconCaretDown"
						alt=""
						aria-hidden="true"
					/>
					<span
						v-if="String(item.id) === String(activeCategoryId) && String(item.id) !== 'nearby'"
						class="category-check"
						aria-hidden="true"
					>
						<img :src="iconCategoryCheck" alt="" />
					</span>
				</button>
			</div>

			<div v-if="String(activeCategoryId) === 'nearby' && regionPanelOpen" class="region-panel">
				<div v-if="regionsLoading" class="region-loading">{{ t(`${I18N_NS}.regionLoading`) }}</div>
				<button
					v-for="region in regions"
					v-else
					:key="region.id"
					type="button"
					class="region-btn"
					:class="{ 'is-active': String(region.id) === String(selectedRegionCode) }"
					@click="handleRegionSelect(region)"
				>
					{{ region.name }}
				</button>
			</div>

			<div class="map-result-scroll">
				<div ref="mapPanelRef" class="map-panel">
					<iframe
						v-if="mapFrameReady"
						class="map-frame"
						:src="googleMapEmbedUrl"
						:title="selectedMapTitle"
						loading="lazy"
						allowfullscreen
						referrerpolicy="no-referrer-when-downgrade"
					></iframe>
					<div v-else class="map-placeholder" aria-hidden="true"></div>
					<div v-if="normalizedMapPins.length" class="map-pins">
						<button
							v-for="(pin, index) in normalizedMapPins"
							:key="`${pin.name}-${index}`"
							type="button"
							class="map-pin"
							:class="{ 'is-active': index === selectedPinIndex }"
							:style="{ left: pin.left, top: pin.top }"
							:aria-label="pin.name"
							@click="selectMapPin(index, pin)"
						>
							<span class="map-pin-dot" aria-hidden="true"></span>
							<span class="map-pin-label">{{ pin.name }}</span>
						</button>
					</div>
					<button
						type="button"
						class="map-fullscreen"
						:aria-label="t(`${I18N_NS}.mapFullscreenAria`)"
						@click="openSelectedMap"
					>
						<img :src="iconMapFullscreen" alt="" />
					</button>
					<div class="map-zoom-controls">
						<button type="button" class="map-zoom-btn" @click="zoomMapIn">
							<img :src="iconZoomIn" alt="" />
						</button>
						<button type="button" class="map-zoom-btn" @click="zoomMapOut">
							<img :src="iconZoomOut" alt="" />
						</button>
					</div>
				</div>

				<div class="result-section">
					<div class="result-header">
						<span class="result-title">{{ listTitle }}</span>
					</div>

					<div v-if="loading" class="result-skeleton-list" aria-hidden="true">
						<div v-for="item in 3" :key="item" class="result-skeleton-card">
							<span class="result-skeleton-cover"></span>
							<div class="result-skeleton-info">
								<span class="result-skeleton-line result-skeleton-line--title"></span>
								<span class="result-skeleton-line result-skeleton-line--meta"></span>
								<span class="result-skeleton-line result-skeleton-line--address"></span>
							</div>
						</div>
					</div>

					<div v-else class="result-list">
						<div
							v-for="item in institutions"
							:key="item.id || `${item.name}-${item.cover}`"
							class="result-card"
							:class="{ 'is-selected': item.id === selectedMerchantId }"
							role="button"
							tabindex="0"
							:ref="el => setResultCardRef(el, item.id)"
							@click="selectInstitution(item)"
							@keydown.enter="selectInstitution(item)"
						>
							<img
								class="result-cover"
								:src="item.cover"
								:alt="item.name"
								loading="lazy"
								decoding="async"
							/>
							<div class="result-info">
								<div class="result-name-row">
									<span class="result-name">{{ item.name }}</span>
									<span v-if="item.type" class="result-type">{{ item.type }}</span>
								</div>
								<div class="result-rating-row">
									<span class="rating-badge">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="28"
											height="28"
											viewBox="0 0 13 12"
											fill="none"
											aria-hidden="true"
										>
											<path
												d="M5.69926 0.250434C5.87923 -0.0834787 6.37077 -0.0834776 6.55074 0.250435L8.13662 3.19284C8.20624 3.32201 8.33387 3.41218 8.48171 3.43665L11.8495 3.99407C12.2317 4.05733 12.3836 4.51192 12.1126 4.78155L9.72496 7.15748C9.62014 7.26178 9.57139 7.40767 9.59315 7.55196L10.0887 10.8389C10.1449 11.2119 9.74725 11.4928 9.39982 11.3256L6.33827 9.85156C6.20388 9.78686 6.04612 9.78686 5.91172 9.85156L2.85018 11.3256C2.50275 11.4928 2.10508 11.2119 2.16132 10.8389L2.65686 7.55196C2.67861 7.40767 2.62986 7.26178 2.52504 7.15748L0.137369 4.78155C-0.133591 4.51192 0.0183045 4.05733 0.400493 3.99407L3.76829 3.43665C3.91614 3.41218 4.04376 3.32201 4.11338 3.19284L5.69926 0.250434Z"
												fill="#F2AF19"
											/>
										</svg>
										{{ item.rating }}
									</span>
									<span class="review-count">
										{{ t(`${I18N_NS}.reviewCount`, { count: item.reviews }) }}
									</span>
								</div>
								<div class="result-address-row">
									<div class="address-text">
										<img :src="iconLocation" alt="" aria-hidden="true" />
										<span>{{ item.address }}</span>
									</div>
									<span v-if="item.distance" class="result-distance">{{ item.distance }}</span>
								</div>
								<p v-if="item.desc" class="result-desc">{{ item.desc }}</p>
								<p v-if="item.favorites" class="result-favorites">{{ item.favorites }}</p>
							</div>
						</div>

						<p v-if="!institutions.length" class="result-empty">
							{{ t(`${I18N_NS}.emptyResult`) }}
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import {
	getInstitutionParams,
	useServiceInstitutionTracking,
} from "../../../pc/composables/useServiceInstitutionTracking";
import iconSearch from "../../../pc/image/Icon.svg";
import iconHot from "../../../pc/image/hot.svg";
import iconLocation from "../../../pc/image/location.svg";
import iconMapFullscreen from "../../../pc/image/icon-map-fullscreen.svg";
import iconZoomIn from "../../../pc/image/icon-zoom-in.svg";
import iconZoomOut from "../../../pc/image/icon-zoom-out.svg";
import iconCaretDown from "../../images/icon-caret-down-figma.svg";
import iconCategoryCheck from "../../images/icon-category-check-figma.svg";
import catAll from "../../../pc/image/category/all.svg";
import catNearby from "../../../pc/image/category/nearby.svg";
import catTaoist from "../../../pc/image/category/taoist.svg";
import catTemple from "../../../pc/image/category/temple.svg";
import catChurch from "../../../pc/image/category/church.svg";
import catMeditation from "../../../pc/image/category/meditation.svg";
import catHealing from "../../../pc/image/category/healing.svg";
import catSpiritual from "../../../pc/image/category/spiritual.svg";
import catCounseling from "../../../pc/image/category/counseling.svg";

const I18N_NS = "serviceInstitution.pc.demo.search";
const { t, locale } = useI18n();
const { track } = useServiceInstitutionTracking("mobile");
const googleMapLanguage = computed(() => (locale.value === "en" ? "en" : "zh-TW"));

const emit = defineEmits(["category-change", "select-institution", "search", "region-select"]);

const FALLBACK_PIN_LAYOUTS = [
	{ left: "48%", top: "42%" },
	{ left: "36%", top: "55%" },
	{ left: "58%", top: "30%" },
	{ left: "44%", top: "64%" },
	{ left: "64%", top: "48%" },
];

const CATEGORY_ICON_MAP = {
	all: catAll,
	nearby: catNearby,
	1: catTaoist,
	2: catTemple,
	3: catChurch,
	4: catMeditation,
	5: catHealing,
	6: catSpiritual,
	7: catCounseling,
};

function resolveCategoryIcon(item) {
	const id = item.id != null ? String(item.id) : "";
	return CATEGORY_ICON_MAP[id] || CATEGORY_ICON_MAP[item.id] || item.icon;
}

const props = defineProps({
	placeholder: {
		type: String,
		default: "",
	},
	searchLabel: {
		type: String,
		default: "",
	},
	categories: {
		type: Array,
		default: () => [],
	},
	activeCategoryId: {
		type: [String, Number],
		default: "all",
	},
	selectedRegionCode: {
		type: [String, Number],
		default: "",
	},
	regions: {
		type: Array,
		default: () => [],
	},
	regionsLoading: {
		type: Boolean,
		default: false,
	},
	selectedMerchantId: {
		type: [String, Number],
		default: null,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	resultCountText: {
		type: String,
		default: "",
	},
	institutions: {
		type: Array,
		default: () => [],
	},
	listTitle: {
		type: String,
		default: "",
	},
});

const selectedPinIndex = ref(0);
const mapZoomLevel = ref(13);
const keyword = ref("");
const mapFrameReady = ref(false);
const regionPanelOpen = ref(false);
const mapPanelRef = ref(null);
const resultCardRefs = new Map();
const impressedResultIds = new Set();
let mapObserver;
let resultObserver;
const impressionTimers = new Map();

function handleSearch() {
	emit("search", keyword.value.trim());
}

function getCategoryLabel(item) {
	if (String(item.id) === "nearby" && props.selectedRegionCode) {
		const selectedRegion = props.regions.find(
			region => String(region.id) === String(props.selectedRegionCode)
		);
		return selectedRegion?.name || item.label;
	}
	return item.label;
}

function handleCategoryClick(item) {
	if (String(item.id) === "nearby") {
		if (String(props.activeCategoryId) === "nearby") {
			regionPanelOpen.value = !regionPanelOpen.value;
			return;
		}
		regionPanelOpen.value = true;
	}
	emit("category-change", item.id);
}

function handleRegionSelect(region) {
	emit("region-select", region);
	regionPanelOpen.value = false;
}

const normalizedMapPins = computed(() =>
	(props.institutions || [])
		.map((item, index) => {
			const fallbackLayout = FALLBACK_PIN_LAYOUTS[index % FALLBACK_PIN_LAYOUTS.length];
			const name = item.name || item.label || item.address || "";
			const query = buildMapQuery(item, name);

			return {
				...item,
				name,
				query,
				left: item.left || fallbackLayout.left,
				top: item.top || fallbackLayout.top,
			};
		})
		.filter(item => item.name && item.query)
);

const selectedMapPin = computed(() => normalizedMapPins.value[selectedPinIndex.value]);

const selectedMapQuery = computed(() => {
	const selectedQuery = selectedMapPin.value?.query;
	const firstInstitutionAddress = props.institutions?.find(item => item.address)?.address;

	return selectedQuery || firstInstitutionAddress || "Hong Kong";
});

const selectedMapTitle = computed(() => selectedMapPin.value?.name || selectedMapQuery.value);

const googleMapEmbedUrl = computed(
	() =>
		`https://www.google.com/maps?q=${encodeURIComponent(selectedMapQuery.value)}&z=${mapZoomLevel.value}&output=embed&hl=${googleMapLanguage.value}`
);

const googleMapUrl = computed(
	() =>
		`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedMapQuery.value)}&hl=${googleMapLanguage.value}`
);

watch(
	() => props.activeCategoryId,
	categoryId => {
		if (String(categoryId) !== "nearby") {
			regionPanelOpen.value = false;
		}
	}
);

watch(
	() => props.institutions.map(item => item.id).join("|"),
	() => {
		resultCardRefs.clear();
		impressedResultIds.clear();
		nextTick(refreshResultObserver);
	}
);

watch(
	() => props.selectedMerchantId,
	merchantId => {
		const nextIndex = normalizedMapPins.value.findIndex(
			item => String(item.id) === String(merchantId)
		);

		if (nextIndex >= 0) {
			selectedPinIndex.value = nextIndex;
		}
	}
);

watch(
	() => normalizedMapPins.value.length,
	length => {
		if (selectedPinIndex.value >= length) {
			selectedPinIndex.value = 0;
		}
	}
);

function buildMapQuery(item, name) {
	if (item.lat != null && item.lng != null) return `${item.lat},${item.lng}`;
	if (item.latitude != null && item.longitude != null) return `${item.latitude},${item.longitude}`;
	if (item.query) return item.query;
	if (item.address) return item.address;

	return [name, item.region].filter(Boolean).join(" ");
}

function selectMapPin(index, pin) {
	mapFrameReady.value = true;
	selectedPinIndex.value = index;
	track("service_map_interaction", {
		interaction_type: "marker_click",
		marker_id: pin.id,
		region_name: pin.name,
		map_zoom_level: "",
	});

	if (pin.id) {
		emit("select-institution", pin.id, "map_marker");
	}
}

function openSelectedMap() {
	if (typeof window === "undefined") return;
	mapFrameReady.value = true;
	track("service_map_control_click", {
		control_type: "fullscreen",
		map_zoom_level: "",
	});
	window.open(googleMapUrl.value, "_blank", "noopener,noreferrer");
}

function zoomMapIn() {
	mapFrameReady.value = true;
	mapZoomLevel.value = Math.min(mapZoomLevel.value + 1, 20);
	track("service_map_control_click", {
		control_type: "zoom_in",
		map_zoom_level: mapZoomLevel.value,
	});
}

function zoomMapOut() {
	mapFrameReady.value = true;
	mapZoomLevel.value = Math.max(mapZoomLevel.value - 1, 3);
	track("service_map_control_click", {
		control_type: "zoom_out",
		map_zoom_level: mapZoomLevel.value,
	});
}

function selectInstitution(item) {
	emit("select-institution", item.id, "result_card");
}

function setResultCardRef(el, id) {
	if (!id) return;
	if (el) {
		resultCardRefs.set(String(id), el);
	} else {
		resultCardRefs.delete(String(id));
	}
}

function refreshResultObserver() {
	if (!import.meta.client) return;
	resultObserver?.disconnect();
	resultObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const id = entry.target.dataset.institutionId;
				if (!id || impressedResultIds.has(id)) return;

				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(id)) return;
					const timer = window.setTimeout(() => {
						const institution = props.institutions.find(item => String(item.id) === id);
						if (!institution || impressedResultIds.has(id)) return;
						impressedResultIds.add(id);
						track(
							"institution_result_impression",
							getInstitutionParams(institution, {
								position: props.institutions.findIndex(item => String(item.id) === id) + 1,
							})
						);
					}, 1000);
					impressionTimers.set(id, timer);
				} else if (impressionTimers.has(id)) {
					window.clearTimeout(impressionTimers.get(id));
					impressionTimers.delete(id);
				}
			});
		},
		{ threshold: [0.5] }
	);

	props.institutions.forEach(item => {
		const el = resultCardRefs.get(String(item.id));
		if (!el) return;
		el.dataset.institutionId = String(item.id);
		resultObserver.observe(el);
	});
}

onMounted(() => {
	if (!import.meta.client) return;
	if (!("IntersectionObserver" in window)) {
		mapFrameReady.value = true;
		nextTick(refreshResultObserver);
		return;
	}
	mapObserver = new IntersectionObserver(
		entries => {
			if (!entries.some(entry => entry.isIntersecting)) return;
			mapFrameReady.value = true;
			track("service_map_view", {
				map_provider: "google",
				location_permission_status: "unknown",
				default_region: selectedMapQuery.value,
			});
			mapObserver?.disconnect();
		},
		{ threshold: 0.5 }
	);
	if (mapPanelRef.value) mapObserver.observe(mapPanelRef.value);
	nextTick(refreshResultObserver);
});

onBeforeUnmount(() => {
	mapObserver?.disconnect();
	resultObserver?.disconnect();
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
});
</script>

<style lang="scss" scoped>
.search-panel {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 48px;
	box-sizing: border-box;

	&.is-loading > :not(.panel-loading-skeleton) {
		visibility: hidden;
	}
}

.panel-loading-skeleton {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: flex;
	flex-direction: column;
	background: #fff;
}

.panel-loading-skeleton span {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: institution-panel-m-shimmer 1.35s ease-in-out infinite;
}

.panel-loading-skeleton__search {
	height: 88px;
	border-radius: 88px;
}

.panel-loading-skeleton__heading {
	width: 210px;
	height: 36px;
	margin-top: 48px;
	border-radius: 8px;
}

.panel-loading-skeleton__categories {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 16px;
	margin-top: 32px;
}

.panel-loading-skeleton__categories span {
	height: 64px;
	border-radius: 8px;
}

.panel-loading-skeleton__map {
	height: 528px;
	margin-top: 48px;
	border-radius: 32px;
}

.panel-loading-skeleton__result-title {
	width: 180px;
	height: 36px;
	margin-top: 48px;
	border-radius: 8px;
}

.panel-loading-skeleton__card {
	display: grid;
	grid-template-columns: 210px minmax(0, 1fr);
	gap: 20px;
	margin-top: 32px;
	padding: 20px;
	border: 1px solid #eee;
	border-radius: 16px;
}

.panel-loading-skeleton__cover {
	height: 170px;
	border-radius: 12px;
}

.panel-loading-skeleton__line {
	height: 22px;
	margin: 22px 0;
	border-radius: 6px;
}

.panel-loading-skeleton__line--title {
	width: 60%;
	height: 30px;
	margin-top: 8px;
}

.panel-loading-skeleton__line--meta {
	width: 42%;
}

@keyframes institution-panel-m-shimmer {
	to {
		background-position: -100% 0;
	}
}

.search-bar {
	position: relative;
	display: flex;
	align-items: center;
	height: 88px;
	padding: 0 144px 0 32px;
	border-radius: 88px;
	background: #f6f6f6;
}

.search-icon {
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	margin-right: 16px;
}

.search-input {
	flex: 1;
	min-width: 0;
	border: none;
	background: transparent;
	color: #333;

	font-size: 28px;
	line-height: 1;
	outline: none;

	&::placeholder {
		color: #a0a0a0;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
}

.search-divider {
	position: absolute;
	right: 144px;
	top: 50%;
	width: 1px;
	height: 44px;
	background: #e5e5e5;
	transform: translateY(-50%);
}

.search-btn {
	position: absolute;
	right: 0;
	top: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 144px;
	height: 88px;
	padding: 0 32px;
	border: none;
	border-radius: 88px;
	background: transparent;
	color: #333;

	font-size: 32px;
	font-weight: 700;
	line-height: 1;
	text-align: center;
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
}

.category-section {
	display: flex;
	flex-direction: column;
	gap: 32px;
}

.map-result-scroll {
	display: flex;
	flex-direction: column;
	gap: 48px;
	height: 1208px;
	max-height: 1208px;
	overflow-x: hidden;
	overflow-y: auto;
	padding-right: 4px;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
}

.category-title {
	display: flex;
	align-items: center;
	gap: 4px;

	img {
		width: 36px;
		height: 36px;
	}

	span {
		color: #333;

		font-size: 36px;
		font-weight: 700;
		line-height: 36px;
	}
}

.category-scroll {
	display: flex;
	gap: 16px;
	overflow-x: auto;
	padding-bottom: 4px;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
}

.category-btn {
	position: relative;
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	gap: 10px;
	min-height: 64px;
	padding: 16px 24px 16px 16px;
	border: 1px solid transparent;
	border-radius: 8px;
	background: #f6f6f6;
	color: #a0a0a0;

	font-size: 28px;
	font-weight: 700;
	line-height: 28px;
	cursor: pointer;

	img {
		width: 32px;
		height: 32px;
		flex-shrink: 0;
	}

	.category-icon {
		filter: grayscale(1) brightness(0) opacity(0.38);
	}

	&.is-nearby {
		padding-right: 16px;
	}

	&.is-active {
		border-color: #333;

		.category-icon {
			filter: grayscale(1) brightness(0) opacity(1);
		}
		background: #fff;
		color: #333;
	}

	&.is-active.is-nearby {
		border-color: #333;
		background: #fff;
		color: #333;
	}
}

.category-btn-label {
	white-space: nowrap;
}

.category-caret {
	flex-shrink: 0;
	width: 32px;
	height: 32px;
}

.category-check {
	position: absolute;
	right: -2px;
	bottom: -2px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	background: transparent;

	img {
		width: 100%;
		height: 100%;
	}
}

.region-panel {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	margin-top: 16px;
}

.region-loading {
	width: 100%;
	color: #a0a0a0;
	font-size: 24px;
	line-height: 1.4;
}

.region-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 56px;
	padding: 12px 20px;
	border: 1px solid #efefef;
	border-radius: 8px;
	background: #fff;
	color: #666;

	font-size: 24px;
	line-height: 1.2;
	cursor: pointer;

	&.is-active {
		border-color: #333;
		color: #333;
		font-weight: 700;
	}
}

.map-panel {
	position: relative;
	flex-shrink: 0;
	height: 528px;
	overflow: hidden;
	border-radius: 32px;
}

.map-frame {
	display: block;
	width: 100%;
	height: 100%;
	border: 0;
	pointer-events: none;
}

.map-placeholder {
	width: 100%;
	height: 100%;
	background: linear-gradient(135deg, rgba(229, 183, 17, 0.08), rgba(127, 147, 229, 0.08)), #f5f5f5;
}

.map-pins {
	position: absolute;
	inset: 0;
	pointer-events: none;
}

.map-pin {
	position: absolute;
	display: flex;
	align-items: center;
	gap: 12px;
	max-width: 260px;
	padding: 10px 16px 10px 12px;
	border: none;
	border-radius: 32px;
	background: #fff;
	box-shadow:
		0 10px 25px rgba(0, 0, 0, 0.03),
		0 26px 20px rgba(0, 0, 0, 0.03),
		0 14px 8px rgba(0, 0, 0, 0.05);
	color: #333;
	font-size: 22px;
	font-weight: 600;
	line-height: 1;
	cursor: pointer;
	pointer-events: auto;
	transform: translate(-50%, -100%);

	&.is-active {
		color: #e5b711;
	}
}

.map-pin-dot {
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	border: 2px solid #fff;
	border-radius: 50%;
	background: #e5b711;
	box-shadow: 0 0 0 2px rgba(229, 183, 17, 0.24);
}

.map-pin-label {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.map-fullscreen {
	position: absolute;
	top: 32px;
	right: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 80px;
	height: 80px;
	padding: 0;
	border: none;
	border-radius: 24px;
	background: #fff;
	box-shadow:
		0 10px 25px rgba(0, 0, 0, 0.03),
		0 26px 20px rgba(0, 0, 0, 0.03),
		0 14px 8px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	img {
		width: 40px;
		height: 40px;
	}
}

.map-zoom-controls {
	position: absolute;
	right: 32px;
	bottom: 32px;
	display: flex;
	flex-direction: column;
	height: 132px;
	overflow: hidden;
	border-radius: 20px;
	background: #fff;
	box-shadow:
		0 10px 25px rgba(0, 0, 0, 0.03),
		0 26px 20px rgba(0, 0, 0, 0.03),
		0 14px 8px rgba(0, 0, 0, 0.05);
	pointer-events: auto;
}

.result-section {
	display: flex;
	flex-direction: column;
	gap: 32px;
	flex-shrink: 0;
}

.map-zoom-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 80px;
	height: 66px;
	padding: 0;
	border: none;
	background: #fff;

	+ .map-zoom-btn {
		border-top: 1px solid #efefef;
	}

	img {
		width: 40px;
		height: 40px;
	}
}

.result-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
}

.result-title {
	color: #333;

	font-size: 32px;
	font-weight: 700;
	line-height: 32px;
}

.result-loading {
	display: flex;
	justify-content: center;
	padding: 80px 0;
}

.result-list {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.result-card {
	display: flex;
	height: 256px;
	cursor: pointer;

	&.is-selected .result-info {
		border-color: #f2af19;
	}
}

.result-cover {
	flex-shrink: 0;
	width: 192px;
	height: 256px;
	border-radius: 16px 0 0 16px;
	object-fit: cover;
	background: #f6f6f6;
}

.result-info {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 256px;
	padding: 16px;
	border: 1px solid #efefef;
	border-left: none;
	border-radius: 0 16px 16px 0;
	box-sizing: border-box;
}

.result-name-row {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 8px;
}

.result-name {
	min-width: 0;
	color: #333;

	font-size: 32px;
	font-weight: 700;
	line-height: 32px;
}

.result-type {
	flex-shrink: 0;
	height: 32px;
	padding: 4px;
	border-radius: 4px;
	background: rgba(229, 183, 17, 0.1);
	color: #e5b711;

	font-size: 24px;
	font-weight: 700;
	line-height: 24px;
}

.result-rating-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

.rating-badge {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	height: 40px;
	padding: 6px 12px 6px 8px;
	border-radius: 4px;
	background: #fff9e5;
	color: #595959;

	font-size: 24px;
	font-weight: 500;
	line-height: 24px;

	svg {
		flex-shrink: 0;
		width: 28px;
		height: 28px;
	}
}

.review-count {
	color: #333;

	font-size: 24px;
	font-weight: 500;
	line-height: 24px;
}

.result-address-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
}

.address-text {
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
	flex: 1;

	img {
		flex-shrink: 0;
		width: 32px;
		height: 32px;
	}

	span {
		min-width: 0;
		overflow: hidden;
		color: #333;

		font-size: 28px;
		font-weight: 500;
		line-height: 28px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.result-distance {
	flex-shrink: 0;
	color: #333;

	font-size: 24px;
	font-weight: 500;
	line-height: 24px;
}

.result-desc {
	margin: 0;
	overflow: hidden;
	color: #595959;

	font-size: 28px;
	font-weight: 500;
	line-height: 28px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.result-favorites {
	margin: 0;
	color: #595959;

	font-size: 24px;
	font-weight: 500;
	line-height: 24px;
}

.result-empty {
	margin: 0;
	text-align: center;
	color: #a0a0a0;
	font-size: 28px;
	line-height: 1.5;
}

.search-panel--en {
	&,
	* {
		overflow-wrap: anywhere;
		word-break: normal;
	}

	.search-bar {
		height: auto;
		min-height: 88px;
		padding: 16px 128px 16px 24px;
	}

	.search-input {
		font-size: 24px;
		line-height: 1.3;
	}

	.search-divider {
		right: 128px;
	}

	.search-btn {
		min-width: 128px;
		height: 100%;
		min-height: 88px;
		padding: 0 20px;

		font-size: 28px;
		line-height: 1.2;
	}

	.category-title span {
		font-size: 30px;
		line-height: 1.2;
	}

	.category-btn {
		font-size: 24px;
		line-height: 1.2;
		min-height: 60px;
		max-width: min(260px, 78vw);
		white-space: normal;
	}

	.category-btn-label {
		min-width: 0;
		white-space: normal;
	}

	.result-title {
		font-size: 28px;
		line-height: 1.25;
	}

	.result-card {
		height: auto;
		min-height: 256px;
	}

	.result-info {
		height: auto;
		min-height: 256px;
	}

	.result-name {
		font-size: 28px;
		line-height: 1.25;
	}

	.result-type {
		height: auto;
		min-height: 28px;
		padding: 4px 6px;

		font-size: 20px;
		line-height: 1.2;
	}

	.rating-badge,
	.review-count,
	.result-distance,
	.result-favorites {
		font-size: 22px;
		line-height: 1.25;
	}

	.address-text span {
		font-size: 24px;
		line-height: 1.3;
		white-space: normal;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.result-desc {
		font-size: 24px;
		line-height: 1.3;
		white-space: normal;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.map-pin {
		max-width: 280px;
		font-size: 20px;
		line-height: 1.2;
	}

	.map-pin-label {
		white-space: normal;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}
}

.result-skeleton-list {
	display: flex;
	flex-direction: column;
	gap: 20px;
}
.result-skeleton-card {
	display: flex;
	gap: 20px;
	padding: 20px;
	border: 1px solid #eee;
	border-radius: 16px;
	background: #fff;
}
.result-skeleton-cover,
.result-skeleton-line {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: institution-m-list-shimmer 1.35s ease-in-out infinite;
}
.result-skeleton-cover {
	flex: 0 0 210px;
	width: 210px;
	height: 170px;
	border-radius: 12px;
}
.result-skeleton-info {
	flex: 1;
	min-width: 0;
	padding-top: 8px;
}
.result-skeleton-line {
	height: 22px;
	margin-bottom: 24px;
	border-radius: 6px;
}
.result-skeleton-line--title {
	width: 60%;
	height: 30px;
}
.result-skeleton-line--meta {
	width: 42%;
}
.result-skeleton-line--address {
	width: 88%;
}
@keyframes institution-m-list-shimmer {
	to {
		background-position: -100% 0;
	}
}
</style>
